
import { useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { RouteData } from './useRouting';
import { useMapMarkers } from './useMapMarkers';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTrafficConditions } from './useTrafficConditions';

export const useRouteRenderer = () => {
  const isMobile = useIsMobile();
  const { addMarkerToMap } = useMapMarkers();
  const { trafficConditions } = useTrafficConditions();

  // Add a route to the map with traffic awareness. useCallback keeps a stable
  // identity — MapPreview lists this in its route-processing effect deps, so an
  // unstable identity here would re-run that effect on every render and flood
  // the geocoding/route edge function with requests.
  const addRouteToMap = useCallback((
    map: mapboxgl.Map,
    route: RouteData,
    fromCoords: [number, number],
    toCoords: [number, number]
  ) => {
    try {
      // Ensure map is loaded before manipulating sources and layers
      if (!map || !map.isStyleLoaded()) {
        return false;
      }

      // Remove any existing route layers and sources safely
      try {
        if (map.getLayer('route-line')) {
          map.removeLayer('route-line');
        }
      } catch (e) {
        // Layer route-line does not exist; continuing.
      }
      
      try {
        if (map.getSource('route')) {
          map.removeSource('route');
        }
      } catch (e) {
        // Source route does not exist; continuing.
      }
      
      // Get color based on traffic congestion
      const congestion = route.congestion || 'low';
      const routeColor = trafficConditions[congestion]?.color || '#7c3aed';
      
      // Add the route line
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: route.geometry
        }
      });
      
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': routeColor,
          'line-width': 4
        }
      });
      
      // Add markers for start and end points
      addMarkerToMap(map, fromCoords, '#22c55e', 'Origin');
      addMarkerToMap(map, toCoords, '#7c3aed', 'Destination');
      
      // Fit the map to show both points and the route
      const bounds = new mapboxgl.LngLatBounds()
        .extend(fromCoords)
        .extend(toCoords);
      
      map.fitBounds(bounds, {
        padding: isMobile ? 30 : 50,
        maxZoom: 15
      });
      
      return true;
    } catch (err) {
      console.error('Error adding route to map:', err);
      return false;
    }
  }, [addMarkerToMap, trafficConditions, isMobile]);

  return {
    addRouteToMap
  };
};
